// src/features/tickets/components/NewTicketModal.tsx
//
// Modal zum Anlegen eines neuen Tickets — aus der TicketsPage heraus aufrufbar.
// Felder:
//   - Kunden-Combobox (debounced Server-Search ueber customerService.searchCustomers,
//     skaliert auf 1.200+ Kunden ohne Bulk-Load)
//   - Betreff
//   - Empfaenger / CC (auto-befuellt aus dem Kunden, editierbar)
//   - Absender-Auswahl „Senden als andere Person" (nur bei aktivem Mailversand;
//     die Außenwirkung folgt dem gewählten Absender, author_*/created_by_*
//     bleiben die echte Person)
//   - Prioritaet
//   - Body
//   - Anhaenge
//   - Checkbox "Per E-Mail an Kunden senden" (Default an)
//
// send_email=true → Mail-Versand via send-ticket-reply.js (Backend persistiert Anhaenge)
// send_email=false → Reines internes Ticket; TicketService.attachToMessage uebernimmt
//                    den Storage-Upload im Frontend.

import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Paperclip, Send, Loader, Building2, Clock } from 'lucide-react';
import { RichTextEmailEditor, prepareEmailHtml } from './RichTextEmailEditor';
import { SenderSelection } from '../../deadlines/components/SenderSelection';
import { useSenderManagement } from '../../deadlines/hooks/useSenderManagement';
import { useAuth } from '../../auth';
import { staffService } from '../../../services/staff/StaffService';
import { customerService } from '../../../services/customer/CustomerService';
import { EmailSenderIdentity } from '../../../services/email/types';
import { Customer } from '../../../shared/types';
import { ticketService } from '../services/TicketService';
import {
  TicketActor,
  TicketAttachmentInput,
  TicketPriority,
  TicketSentAs
} from '../types';
import type { ProofreadContext } from '../services/TicketProofreadService';

interface NewTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Wird nach erfolgreicher Anlage aufgerufen (Parent laedt die Ticket-Liste neu). */
  onSuccess: () => void;
}

const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: 'low', label: 'Niedrig' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Hoch' }
];

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;
const SEARCH_LIMIT = 20;

export const NewTicketModal: React.FC<NewTicketModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();

  // Actor
  const [currentStaff, setCurrentStaff] = useState<EmailSenderIdentity | null>(null);

  // Customer-Combobox
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searching, setSearching] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  // Form-Felder
  const [subject, setSubject] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachments, setAttachments] = useState<TicketAttachmentInput[]>([]);
  const [sendEmail, setSendEmail] = useState(true);

  // UI-Status
  const [attaching, setAttaching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<'now' | 'later'>('now');
  const [scheduledLocal, setScheduledLocal] = useState('');
  // Ob aktuell ein nicht uebernommener KI-Vorschlag offen ist (vom ProofreadButton
  // gemeldet) — fuer die Rueckfrage vor dem Anlegen/Senden.
  const [proofreadPending, setProofreadPending] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Absender-Auswahl „Senden als andere Person" (nur bei aktivem Mailversand
  // relevant): alle aktiven Mitarbeiter ohne Foto-Pflicht (requirePhoto=false —
  // die Ticket-Signatur hat einen Text-Fallback ohne Foto). Das Laden ist auf
  // isOpen UND eine aufgelöste User-E-Mail gegated, damit das Smart-Matching
  // des Hooks sicher den eingeloggten Nutzer als Default trifft. Der Hook
  // setzt sich beim Schließen des Modals selbst zurück.
  const {
    senders,
    selectedSender,
    setSelectedSender,
    loading: sendersLoading,
    error: sendersError
  } = useSenderManagement(isOpen && Boolean(user?.email), user?.email, false);

  // Aktuellen Mitarbeiter aufloesen
  useEffect(() => {
    if (!isOpen) return;
    const loadStaff = async () => {
      if (!user?.email) return;
      try {
        const all = await staffService.getActiveStaff();
        const me = all.find(s => s.email.toLowerCase() === user.email.toLowerCase());
        if (me) setCurrentStaff(me);
      } catch (err) {
        console.error('[NewTicketModal] Staff-Load-Error:', err);
      }
    };
    loadStaff();
  }, [isOpen, user?.email]);

  // Debounced Customer-Search
  useEffect(() => {
    // Wenn ein Kunde bereits ausgewaehlt ist, nicht weiter suchen
    if (selectedCustomer) return;

    const trimmed = customerSearch.trim();
    if (trimmed.length < SEARCH_MIN_CHARS) {
      setCustomerSuggestions([]);
      return;
    }

    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await customerService.searchCustomers(trimmed, SEARCH_LIMIT);
        setCustomerSuggestions(results);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [customerSearch, selectedCustomer]);

  // Reset bei Schliessen
  useEffect(() => {
    if (!isOpen) {
      setCustomerSearch('');
      setCustomerSuggestions([]);
      setSelectedCustomer(null);
      setSuggestionsOpen(false);
      setSubject('');
      setToEmail('');
      setCcEmail('');
      setPriority('normal');
      setBodyHtml('');
      setAttachments([]);
      setSendEmail(true);
      setScheduleMode('now');
      setScheduledLocal('');
      setProofreadPending(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Prüft, ob ein Sender die handelnde Person selbst ist — primär per Staff-ID,
  // ersatzweise per E-Mail (falls die Staff-Auflösung ausnahmsweise scheiterte).
  // Nur bei Abweichung wird sent_as gesetzt.
  const isSelectedSenderSelf = (sender: EmailSenderIdentity): boolean => {
    if (currentStaff?.id && sender.id === currentStaff.id) return true;
    const ownEmail = (user?.email ?? '').trim().toLowerCase();
    return !!ownEmail && (sender.email || '').trim().toLowerCase() === ownEmail;
  };

  // Kunden-Auswahl: Empfaenger-Felder befuellen, falls leer
  const handleSelectCustomer = (c: Customer) => {
    setSelectedCustomer(c);
    setCustomerSearch(c.company);
    setCustomerSuggestions([]);
    setSuggestionsOpen(false);
    if (c.email && !toEmail.trim()) setToEmail(c.email);
    if (c.email_cc && !ccEmail.trim()) setCcEmail(c.email_cc);
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerSuggestions([]);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setAttaching(true);
    try {
      const newAttachments: TicketAttachmentInput[] = [];
      for (let i = 0; i < files.length; i++) {
        const att = await fileToAttachmentInput(files[i]);
        if (att) newAttachments.push(att);
      }
      setAttachments(prev => [...prev, ...newAttachments]);
    } catch (err) {
      console.error('[NewTicketModal] Fehler beim Lesen der Anhaenge:', err);
      alert('Anhang konnte nicht gelesen werden.');
    } finally {
      setAttaching(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const { bodyHtml: cleanHtml, bodyText: plainText, inlineImages } = prepareEmailHtml(bodyHtml);

    // Rueckfrage, wenn ein KI-Vorschlag erzeugt, aber nicht uebernommen wurde:
    // sonst wuerde versehentlich der unkorrigierte Originaltext verwendet. Gilt
    // fuer alle Versand-Wege und das reine interne Anlegen (ohne Mailversand).
    if (proofreadPending) {
      const proceed = window.confirm(
        'Es liegt ein nicht übernommener KI-Vorschlag vor. Wenn Du fortfährst, wird der unkorrigierte Originaltext verwendet. Trotzdem fortfahren?'
      );
      if (!proceed) return;
    }

    if (!subject.trim()) { alert('Betreff ist erforderlich.'); return; }
    if (!plainText.trim()) { alert('Nachrichtentext ist erforderlich.'); return; }
    if (sendEmail) {
      const toTokens = parseEmails(toEmail);
      if (toTokens.length === 0) { alert('Empfänger-Adresse ist erforderlich, wenn die Mail versendet werden soll.'); return; }
      const invalidTo = toTokens.find((addr) => !isValidEmail(addr));
      if (invalidTo) { alert(`Ungültige Empfänger-Adresse: „${invalidTo}". Bitte korrigieren oder entfernen.`); return; }
      const invalidCc = parseEmails(ccEmail).find((addr) => !isValidEmail(addr));
      if (invalidCc) { alert(`Ungültige CC-Adresse: „${invalidCc}". Bitte korrigieren oder entfernen.`); return; }
    }

    const isScheduled = sendEmail && scheduleMode === 'later';
    if (isScheduled && !scheduledLocal) { alert('Bitte einen Sendezeitpunkt wählen.'); return; }

    const actor: TicketActor = {
      staff_id: currentStaff?.id ?? null,
      staff_email: currentStaff?.email ?? user?.email ?? '',
      staff_full_name: currentStaff?.fullName
    };

    // Außenidentität „Senden als": nur gesetzt, wenn bei aktivem Mailversand ein
    // vom eingeloggten Nutzer abweichender Absender gewählt wurde. Ohne Auswahl
    // (z. B. wenn die Absender-Liste nicht geladen werden konnte) wird als man
    // selbst gesendet — Bestandsverhalten.
    const sentAs: TicketSentAs | undefined =
      sendEmail && selectedSender && !isSelectedSenderSelf(selectedSender)
        ? { staff_id: selectedSender.id, name: selectedSender.fullName }
        : undefined;

    setSubmitting(true);
    try {
      if (isScheduled) {
        const scheduleResult = await ticketService.scheduleNewTicket({
          customer_id: selectedCustomer?.id ?? null,
          subject: subject.trim(),
          body_text: plainText,
          body_html: cleanHtml,
          priority,
          to_emails: parseEmails(toEmail),
          cc_emails: ccEmail.trim() ? parseEmails(ccEmail) : undefined,
          scheduled_at_local: scheduledLocal,
          attachments: attachments.length > 0 ? attachments : undefined,
          inline_images: inlineImages.length > 0 ? inlineImages : undefined,
          sent_as: sentAs
        }, actor);

        if (!scheduleResult.success) {
          alert(`Ticket/Versand konnte nicht geplant werden: ${scheduleResult.error}`);
          return;
        }

        onSuccess();
        onClose();
        return;
      }

      const result = await ticketService.createTicket({
        customer_id: selectedCustomer?.id ?? null,
        subject: subject.trim(),
        body_text: plainText,
        body_html: cleanHtml,
        priority,
        send_email: sendEmail,
        to_emails: sendEmail ? parseEmails(toEmail) : undefined,
        cc_emails: sendEmail && ccEmail.trim() ? parseEmails(ccEmail) : undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        inline_images: inlineImages.length > 0 ? inlineImages : undefined,
        sent_as: sentAs
      }, actor);

      if (!result.success) {
        alert(`Ticket konnte nicht angelegt werden: ${result.error}`);
        return;
      }

      if (result.mail_error) {
        alert(`Ticket angelegt, aber Mail-Versand fehlgeschlagen: ${result.mail_error}`);
      }

      onSuccess();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  // Stellt den Kontext fuer die KI-Pruefung (Voll-Modus) zusammen. Beim Anlegen
  // gibt es noch keinen Verlauf und keinen Anfragenden; bekannt sind der
  // Absendername, die aktuell angehaengten Dateinamen und — sofern ein Kunde
  // gewählt ist — eine aus dessen Stammdaten abgeleitete Anrede (salutation),
  // die der Service deterministisch bildet.
  // „Senden als": Duz-Gate (Staff-ID) und Grußformel-Name richten sich bei
  // aktivem Mailversand nach dem GEWÄHLTEN Absender; sonst nach dem
  // eingeloggten Mitarbeiter — Bestandsverhalten.
  // is_scheduled_send meldet dem Server, ob gerade ein terminierter Versand
  // eingestellt ist; er unterdrückt dann die Freitags-Grußformel.
  const getProofreadContext = async (): Promise<ProofreadContext> => {
    const identityStaffId = sendEmail && selectedSender ? selectedSender.id : (currentStaff?.id ?? null);
    const identityName = sendEmail && selectedSender ? selectedSender.fullName : (currentStaff?.fullName ?? null);
    return {
      requester_name: null,
      salutation: await ticketService.buildSalutationForCustomer(selectedCustomer, identityStaffId),
      sender_name: identityName,
      is_scheduled_send: sendEmail && scheduleMode === 'later',
      attachments: attachments.map(a => a.filename),
      thread: []
    };
  };

  // E-Mail-Validierung der Empfaengerfelder (nur relevant, wenn die Mail versendet wird).
  const toInvalid = sendEmail && toEmail.trim().length > 0 && parseEmails(toEmail).some((addr) => !isValidEmail(addr));
  const ccInvalid = sendEmail && ccEmail.trim().length > 0 && parseEmails(ccEmail).some((addr) => !isValidEmail(addr));

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '720px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 24px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
            Neues Ticket
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              background: 'none',
              border: 'none',
              cursor: submitting ? 'not-allowed' : 'pointer',
              color: '#6B7280',
              padding: '4px',
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 24px'
        }}>
          {/* Kunden-Combobox */}
          <FormField label="Kunde">
            {selectedCustomer ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 12px',
                backgroundColor: '#EFF6FF',
                border: '1px solid #BFDBFE',
                borderRadius: '6px'
              }}>
                <Building2 size={16} color="#1E40AF" />
                <span style={{ flex: 1, fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                  {selectedCustomer.company}
                </span>
                {selectedCustomer.email && (
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>
                    {selectedCustomer.email}
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleClearCustomer}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6B7280',
                    padding: '2px',
                    display: 'inline-flex'
                  }}
                  title="Auswahl zurücksetzen"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'relative' }}>
                  <Search
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9CA3AF',
                      pointerEvents: 'none'
                    }}
                  />
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setSuggestionsOpen(true);
                    }}
                    onFocus={() => setSuggestionsOpen(true)}
                    placeholder="Firma, Name oder E-Mail eingeben (mind. 2 Zeichen)..."
                    style={{
                      width: '100%',
                      padding: '8px 10px 8px 34px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {searching && (
                    <Loader
                      size={14}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9CA3AF',
                        animation: 'spin 1s linear infinite'
                      }}
                    />
                  )}
                </div>

                {/* Vorschlags-Dropdown */}
                {suggestionsOpen && customerSearch.trim().length >= SEARCH_MIN_CHARS && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                    maxHeight: '240px',
                    overflowY: 'auto',
                    zIndex: 10
                  }}>
                    {customerSuggestions.length === 0 && !searching ? (
                      <div style={{ padding: '10px 12px', fontSize: '13px', color: '#9CA3AF' }}>
                        Kein Kunde gefunden.
                      </div>
                    ) : (
                      customerSuggestions.map(c => (
                        <CustomerSuggestionRow
                          key={c.id}
                          customer={c}
                          onClick={() => handleSelectCustomer(c)}
                        />
                      ))
                    )}
                  </div>
                )}
                <div style={{ marginTop: '4px', fontSize: '12px', color: '#9CA3AF' }}>
                  Leer lassen für Pool-Ticket (ohne Kundenzuordnung).
                </div>
              </div>
            )}
          </FormField>

          {/* Betreff */}
          <FormField label="Betreff *">
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Kurzer Betreff..."
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </FormField>

          {/* Absender + Empfaenger + CC (nur wenn sendEmail) */}
          {sendEmail && (
            <>
              {/* Absender-Auswahl: „Senden als andere Person" */}
              <SenderSelection
                senders={senders}
                selectedSender={selectedSender}
                onSenderChange={setSelectedSender}
                disabled={submitting}
                loading={sendersLoading}
                error={sendersError}
              />

              <FormField label="Empfänger *">
                <input
                  type="text"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="empfänger@example.com (mehrere mit Komma trennen)"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${toInvalid ? '#DC2626' : '#D1D5DB'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                {toInvalid && (
                  <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '4px' }}>
                    Ungültige E-Mail-Adresse.
                  </div>
                )}
              </FormField>

              <FormField label="CC">
                <input
                  type="text"
                  value={ccEmail}
                  onChange={(e) => setCcEmail(e.target.value)}
                  placeholder="optional"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${ccInvalid ? '#DC2626' : '#D1D5DB'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                {ccInvalid && (
                  <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '4px' }}>
                    Ungültige E-Mail-Adresse.
                  </div>
                )}
              </FormField>
            </>
          )}

          {/* Prioritaet */}
          <FormField label="Priorität">
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              {PRIORITY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FormField>

          {/* Nachricht */}
          <FormField label="Nachricht *">
            <RichTextEmailEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              placeholder="Nachrichtentext..."
              proofreadMode="full"
              getProofreadContext={getProofreadContext}
              onProofreadPendingChange={setProofreadPending}
            />
          </FormField>

          {/* Anhaenge */}
          <FormField label="Anhänge">
            <div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={attaching || submitting}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: 'white',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#374151',
                  cursor: attaching || submitting ? 'not-allowed' : 'pointer',
                  opacity: attaching || submitting ? 0.6 : 1
                }}
              >
                {attaching
                  ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  : <Paperclip size={14} />}
                Datei hinzufügen
              </button>

              {attachments.length > 0 && (
                <div style={{
                  marginTop: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  {attachments.map((att, idx) => (
                    <AttachmentItem
                      key={`${att.filename}-${idx}`}
                      filename={att.filename}
                      fileSize={att.file_size}
                      onRemove={() => handleRemoveAttachment(idx)}
                      disabled={submitting}
                    />
                  ))}
                </div>
              )}
            </div>
          </FormField>

          {/* Mailversand-Toggle */}
          <div style={{
            marginTop: '8px',
            padding: '10px 12px',
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '6px'
          }}>
            <label style={{
              display: 'flex',
              gap: '8px',
              cursor: 'pointer',
              alignItems: 'flex-start'
            }}>
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                style={{ marginTop: '3px' }}
              />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                  Nachricht per E-Mail an den Kunden senden
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                  Wenn deaktiviert, wird das Ticket nur intern angelegt — keine E-Mail.
                </div>
              </div>
            </label>
          </div>

          {/* Geplant senden (nur bei aktivem Mailversand) */}
          {sendEmail && (
            <div style={{
              marginTop: '8px',
              padding: '10px 12px',
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '6px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{
                  display: 'inline-flex',
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  borderRadius: '6px',
                  padding: '3px'
                }}>
                  <button
                    type="button"
                    onClick={() => setScheduleMode('now')}
                    disabled={submitting}
                    style={scheduleSegmentStyle(scheduleMode === 'now')}
                  >
                    Sofort senden
                  </button>
                  <button
                    type="button"
                    onClick={() => setScheduleMode('later')}
                    disabled={submitting}
                    style={scheduleSegmentStyle(scheduleMode === 'later')}
                  >
                    <Clock size={13} />
                    Geplant senden
                  </button>
                </div>
                {scheduleMode === 'later' && (
                  <input
                    type="datetime-local"
                    value={scheduledLocal}
                    min={berlinNowLocalString()}
                    onChange={(e) => setScheduledLocal(e.target.value)}
                    disabled={submitting}
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#374151',
                      backgroundColor: 'white'
                    }}
                  />
                )}
              </div>
              {scheduleMode === 'later' && (
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>
                  Das Ticket wird sofort angelegt; die erste Nachricht geht zum gewählten Zeitpunkt (Berliner Zeit) raus.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 16px',
              backgroundColor: '#F3F4F6',
              color: '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: submitting ? 0.6 : 1
            }}
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || attaching || (sendEmail && scheduleMode === 'later' && !scheduledLocal) || toInvalid || ccInvalid}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 18px',
              backgroundColor: (submitting || (sendEmail && scheduleMode === 'later' && !scheduledLocal) || toInvalid || ccInvalid) ? '#9CA3AF' : '#2563EB',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (submitting || attaching || (sendEmail && scheduleMode === 'later' && !scheduledLocal) || toInvalid || ccInvalid) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {submitting
              ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
              : ((sendEmail && scheduleMode === 'later') ? <Clock size={14} /> : <Send size={14} />)}
            {!sendEmail
              ? 'Ticket anlegen'
              : (scheduleMode === 'later' ? 'Ticket anlegen & planen' : 'Ticket anlegen & senden')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Form-Field (Label + Slot)
// ============================================

interface FormFieldProps {
  label: string;
  children: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({ label, children }) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={{
      display: 'block',
      fontSize: '13px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '4px'
    }}>
      {label}
    </label>
    {children}
  </div>
);

// ============================================
// Customer-Suggestion-Row
// ============================================

const CustomerSuggestionRow: React.FC<{ customer: Customer; onClick: () => void }> = ({ customer, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const ansprechpartner = [customer.vorname, customer.nachname].filter(Boolean).join(' ').trim();

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '8px 12px',
        backgroundColor: hovered ? '#F3F4F6' : 'transparent',
        cursor: 'pointer',
        borderBottom: '1px solid #F3F4F6'
      }}
    >
      <div style={{
        fontSize: '14px',
        color: '#111827',
        fontWeight: '500'
      }}>
        {customer.company}
      </div>
      <div style={{
        fontSize: '12px',
        color: '#6B7280',
        display: 'flex',
        gap: '8px',
        marginTop: '2px'
      }}>
        {ansprechpartner && <span>{ansprechpartner}</span>}
        {ansprechpartner && customer.email && <span>·</span>}
        {customer.email && <span>{customer.email}</span>}
      </div>
    </div>
  );
};

// ============================================
// Attachment-Item
// ============================================

interface AttachmentItemProps {
  filename: string;
  fileSize: number;
  onRemove: () => void;
  disabled: boolean;
}

const AttachmentItem: React.FC<AttachmentItemProps> = ({ filename, fileSize, onRemove, disabled }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    fontSize: '13px'
  }}>
    <Paperclip size={14} color="#6B7280" style={{ flexShrink: 0 }} />
    <span style={{
      flex: 1,
      color: '#374151',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }}>
      {filename}
    </span>
    <span style={{ color: '#9CA3AF', flexShrink: 0 }}>
      {formatFileSize(fileSize)}
    </span>
    <button
      type="button"
      onClick={onRemove}
      disabled={disabled}
      style={{
        background: 'none',
        border: 'none',
        padding: '2px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: '#9CA3AF',
        display: 'inline-flex',
        alignItems: 'center'
      }}
      title="Anhang entfernen"
    >
      <X size={14} />
    </button>
  </div>
);

// ============================================
// Helpers
// ============================================

async function fileToAttachmentInput(file: File): Promise<TicketAttachmentInput | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        resolve(null);
        return;
      }
      const commaIdx = result.indexOf(',');
      const base64 = commaIdx >= 0 ? result.substring(commaIdx + 1) : '';
      resolve({
        filename: file.name,
        content_base64: base64,
        content_type: file.type || 'application/octet-stream',
        file_size: file.size
      });
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

function parseEmails(input: string): string[] {
  return input.split(',').map(s => s.trim()).filter(Boolean);
}

/** Format-Pruefung einer einzelnen E-Mail-Adresse (genau ein @, keine Leerzeichen, Punkt in der Domain). */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

/**
 * Aktuelle Berliner Wanduhrzeit als "YYYY-MM-DDTHH:mm" fuer das min-Attribut des
 * datetime-local-Felds (UI-seitige Schranke; die eigentliche Pruefung passiert
 * serverseitig in scheduleNewTicket).
 */
function berlinNowLocalString(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(new Date());
  const m: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') m[p.type] = p.value;
  }
  return `${m.year}-${m.month}-${m.day}T${m.hour}:${m.minute}`;
}

/** Style fuer die Sofort/Geplant-Segmentschalter. */
function scheduleSegmentStyle(active: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 10px',
    backgroundColor: active ? 'white' : 'transparent',
    color: active ? '#2563EB' : '#6B7280',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: active ? 600 : 500,
    cursor: 'pointer',
    boxShadow: active ? '0 1px 2px 0 rgba(0,0,0,0.06)' : 'none'
  };
}
